import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { Shield, WifiOff, Lock, Server, Zap } from 'lucide-react';

const Features = () => {
    return (
        <section id="features" className="py-24 md:py-32 px-6 md:px-16 bg-[#0A0A14] relative z-20 rounded-t-[3rem] -mt-[3rem] shadow-[0_-20px_40px_rgba(10,10,20,0.8)] border-t border-[#7B61FF]/20">
            <div className="max-w-7xl mx-auto">
                {/* Section header — customer outcome focused */}
                <div className="mb-20 max-w-2xl">
                    <h2 className="text-[#F0EFF4] font-bold text-4xl md:text-5xl tracking-tight mb-4">
                        Three reasons your competitors{' '}
                        <span className="font-drama text-[#7B61FF]">are watching you.</span>
                    </h2>
                    <p className="text-[#F0EFF4]/70 text-lg">
                        RoofClaw delivers local AI sovereignty, deep API connectivity, and execution that never clocks out — all in a single deployment.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <ShufflerCard />
                    <TypewriterCard />
                    <SchedulerCard />
                </div>
            </div>
        </section>
    );
};

/* --- CARD 1: Data Sovereignty Shuffler --- */
const ShufflerCard = () => {
    const [items, setItems] = useState([
        { id: 1, title: 'IP Cloaking Active', desc: 'Invisible to public routing. Complete network obfuscation.', icon: WifiOff },
        { id: 2, title: 'Zero-Trust Tunnel', desc: 'Every connection verified. Hackers see zero attack surface.', icon: Lock },
        { id: 3, title: 'No SSH on Delivery', desc: 'We cannot log in once it ships. Your data. Your control.', icon: Shield },
    ]);

    useEffect(() => {
        const interval = setInterval(() => {
            setItems(prev => {
                const newArr = [...prev];
                const last = newArr.pop();
                newArr.unshift(last);
                return newArr;
            });
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="bg-[#18181B] border border-[#7B61FF]/30 backdrop-blur-xl rounded-[2rem] p-8 shadow-[0_0_30px_rgba(123,97,255,0.05)] flex flex-col h-[420px] relative overflow-hidden group">
            <div className="mb-auto">
                {/* Outcome-first heading */}
                <h3 className="font-bold text-xl text-[#F0EFF4] mb-2">Your Data. Your Desk.</h3>
                <p className="text-sm text-[#F0EFF4]/60 leading-relaxed max-w-[250px]">
                    No cloud VMs. No SSH backdoors. Your customer CRM lives on hardware you physically own — not a stranger's server.
                </p>
            </div>

            <div className="relative h-[200px] mt-8 w-full perspective-1000">
                {items.map((item, index) => {
                    const IconObj = item.icon;
                    const isTop = index === 0;
                    return (
                        <div
                            key={item.id}
                            className="absolute w-full rounded-[1.5rem] p-5 shadow-lg flex items-center gap-4 transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)] border border-[#7B61FF]/20"
                            style={{
                                backgroundColor: isTop ? '#2A2A35' : '#0A0A14',
                                transform: `translateY(${index * 20}px) scale(${1 - index * 0.05})`,
                                zIndex: 10 - index,
                                opacity: 1 - index * 0.2
                            }}
                        >
                            <div className={`p-2 rounded-full ${isTop ? 'bg-[#7B61FF]/20 text-[#7B61FF]' : 'bg-[#F0EFF4]/5 text-[#F0EFF4]/20'}`}>
                                <IconObj size={20} />
                            </div>
                            <div>
                                <div className="font-semibold text-sm text-[#F0EFF4] mb-1">{item.title}</div>
                                <div className={`text-xs ${isTop ? 'text-[#F0EFF4]/60' : 'text-[#F0EFF4]/20'}`}>{item.desc}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* --- CARD 2: API Integration Typewriter --- */
const TypewriterCard = () => {
    const [text, setText] = useState('');
    const [lineIndex, setLineIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);

    const lines = [
        "[SYS] INITIATING API HANDSHAKE...",
        "[API] HUBSPOT CRM → CONNECTED [OK]",
        "[API] JUSTCALL VOIP → CONNECTED [OK]",
        "[API] ZUPER FIELD OPS → CONNECTED [OK]",
        "[UX] LOADING ROOFING SOPs INTO CONTEXT...",
        "[UX] 3-HOUR TRAINING PROTOCOL READY.",
        "[SYS] YOUR CHIEF OF STAFF IS LIVE.",
    ];

    useEffect(() => {
        if (lineIndex >= lines.length) {
            setTimeout(() => {
                setText('');
                setLineIndex(0);
                setCharIndex(0);
            }, 4000);
            return;
        }

        const currentLine = lines[lineIndex];

        if (charIndex < currentLine.length) {
            const timeout = setTimeout(() => {
                setText(prev => prev + currentLine[charIndex]);
                setCharIndex(c => c + 1);
            }, Math.random() * 40 + 20);
            return () => clearTimeout(timeout);
        } else {
            const timeout = setTimeout(() => {
                setText(prev => prev + '\n');
                setLineIndex(l => l + 1);
                setCharIndex(0);
            }, 800);
            return () => clearTimeout(timeout);
        }
    }, [charIndex, lineIndex]);

    return (
        <div className="bg-[#18181B] border border-[#7B61FF]/30 backdrop-blur-xl rounded-[2rem] p-8 shadow-[0_0_30px_rgba(123,97,255,0.05)] flex flex-col h-[420px]">
            <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-full bg-[#7B61FF] animate-pulse"></span>
                    <span className="text-[#7B61FF] font-data text-xs font-bold tracking-widest uppercase">Live Connection Feed</span>
                </div>
                {/* Outcome-first heading */}
                <h3 className="font-bold text-xl text-[#F0EFF4] mb-2">Plug Into Your Existing Stack</h3>
                <p className="text-sm text-[#F0EFF4]/60 leading-relaxed">
                    We bridge your agent to HubSpot, JustCall, Zuper, Sumo — or any software with an open API. Your SOPs. Connected.
                </p>
            </div>

            <div className="flex-1 bg-[#0A0A14] rounded-[1rem] p-5 overflow-hidden border border-[#7B61FF]/10 shadow-inner relative">
                <pre className="font-data text-xs text-[#7B61FF] whitespace-pre-wrap relative z-10 leading-relaxed">
                    {text}
                    <span className="w-2 h-4 bg-[#7B61FF] inline-block animate-[pulse_1s_step-end_infinite] ml-1"></span>
                </pre>
            </div>
        </div>
    );
};

/* --- CARD 3: Storm Surge Scheduler --- */
const SchedulerCard = () => {
    const containerRef = useRef(null);
    const cursorRef = useRef(null);
    const dayRef = useRef(null);
    const btnRef = useRef(null);

    useEffect(() => {
        let ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1, repeatDelay: 1 });

            tl.to(cursorRef.current, { x: 50, y: 70, duration: 1, ease: "power2.inOut" })
                .to(cursorRef.current, { x: 135, y: 110, duration: 0.8, ease: "power3.inOut" })
                .to(cursorRef.current, { scale: 0.8, duration: 0.1, yoyo: true, repeat: 1 })
                .to(dayRef.current, { backgroundColor: '#7B61FF', color: '#FFF', duration: 0.2 }, "-=0.2")
                .to(cursorRef.current, { x: 140, y: 195, duration: 0.8, ease: "power2.inOut", delay: 0.4 })
                .to(cursorRef.current, { scale: 0.8, duration: 0.1, yoyo: true, repeat: 1 })
                .to(btnRef.current, { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1 }, "-=0.2")
                .to(btnRef.current, { backgroundColor: '#7B61FF', color: '#FFF', duration: 0.2 }, "-=0.2")
                .to(cursorRef.current, { opacity: 0, duration: 0.5, delay: 0.5 })
                .to(dayRef.current, { backgroundColor: 'transparent', color: '#F0EFF4', duration: 0.2 }, "-=0.5")
                .to(btnRef.current, { backgroundColor: 'transparent', color: '#F0EFF4', duration: 0.2 }, "-=0.5")
                .set(cursorRef.current, { x: -20, y: -20, opacity: 1 });

        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <div className="bg-[#18181B] border border-[#7B61FF]/30 backdrop-blur-xl rounded-[2rem] p-8 shadow-[0_0_30px_rgba(123,97,255,0.05)] flex flex-col h-[420px]" ref={containerRef}>
            <div className="mb-6">
                {/* Outcome-first heading */}
                <h3 className="font-bold text-xl text-[#F0EFF4] mb-2">Handles Storm Surge While You Sleep</h3>
                <p className="text-sm text-[#F0EFF4]/60 leading-relaxed">
                    When hail hits and leads spike 1,000%, your agent scales. It never quits at 5 PM, never drops a follow-up, never burns out.
                </p>
            </div>

            <div className="flex-1 border border-[#7B61FF]/20 rounded-[1.5rem] bg-[#0A0A14] p-6 relative overflow-hidden flex flex-col justify-center items-center">

                {/* Animated Cursor */}
                <div ref={cursorRef} className="absolute z-50 w-6 h-6 -top-5 -left-5">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4L11.5312 21.0765C11.8344 21.7645 12.8128 21.7225 13.0645 21.0064L15.3456 14.5165C15.466 14.1741 15.7346 13.9056 16.077 13.7852L22.5669 11.5041C23.283 11.2524 23.3249 10.2741 22.637 9.97086L5.56044 2.43956C4.8967 2.14665 4.14665 2.8967 4.43956 3.56044L4 4Z" fill="#0A0A14" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>

                {/* Day Grid */}
                <div className="w-full flex justify-between px-2 mb-8 bg-[#18181B] rounded-xl p-2 shadow-sm border border-[#7B61FF]/20">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                        <div key={i} ref={i === 4 ? dayRef : null} className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold text-[#F0EFF4] bg-transparent transition-colors">
                            {day}
                        </div>
                    ))}
                </div>

                {/* Task steps — now on-brand for roofing */}
                <div className="w-full space-y-3">
                    <div className="flex items-center gap-3 w-full bg-[#18181B] p-3 rounded-xl shadow-sm border border-[#7B61FF]/20 opacity-60">
                        <Zap size={16} className="text-[#F0EFF4]" />
                        <div className="text-xs font-medium text-[#F0EFF4]">1. Hail Storm Alert Detected</div>
                    </div>
                    <div className="flex items-center gap-3 w-full bg-[#18181B] p-3 rounded-xl shadow-sm border border-[#7B61FF]/20 opacity-80">
                        <Server size={16} className="text-[#F0EFF4]" />
                        <div className="text-xs font-medium text-[#F0EFF4]">2. Lead Filed in HubSpot</div>
                    </div>
                    <div ref={btnRef} className="flex items-center gap-3 w-full border border-[#7B61FF]/30 p-3 rounded-xl bg-transparent transition-colors text-[#F0EFF4]">
                        <Lock size={16} className="text-current" />
                        <div className="text-xs font-semibold text-current">3. SMS Sent via JustCall</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Features;
